import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import CreatePipelineForm from '../../components/CreatePipelineForm';
import EditPipelineForm from '../../components/EditPipelineForm';
import ScheduleWindowForm from '../../components/ScheduleWindowForm';
import ScheduleWindowList from '../../components/ScheduleWindowList';
import StatusBadge from '../../components/StatusBadge';
import {
  getPipelines,
  startPipeline,
  stopPipeline,
  deletePipeline,
} from '../../api/client';

function Admin() {
  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleFormOpen, setScheduleFormOpen] = useState(false);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [scheduleListKey, setScheduleListKey] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPipeline, setEditingPipeline] = useState(null);

  useEffect(() => {
    fetchPipelines();
  }, []);

  const fetchPipelines = async () => {
    try {
      setLoading(true);
      const data = await getPipelines();
      setPipelines(data);
    } catch (error) {
      console.error('Failed to fetch pipelines:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStart = async (pipelineId) => {
    try {
      await startPipeline(pipelineId);
      fetchPipelines();
    } catch (error) {
      console.error('Failed to start pipeline:', error);
    }
  };

  const handleStop = async (pipelineId) => {
    try {
      await stopPipeline(pipelineId);
      fetchPipelines();
    } catch (error) {
      console.error('Failed to stop pipeline:', error);
    }
  };

  const handleDelete = async (pipelineId) => {
    if (window.confirm('Are you sure you want to delete this pipeline?')) {
      try {
        await deletePipeline(pipelineId);
        fetchPipelines();
      } catch (error) {
        console.error('Failed to delete pipeline:', error);
      }
    }
  };

  const handleCreateSuccess = () => {
    setCreateDialogOpen(false);
    fetchPipelines();
  };

  const handleOpenSchedules = (pipeline) => {
    setSelectedPipeline(pipeline);
    setScheduleDialogOpen(true);
  };

  const handleCloseSchedules = () => {
    setScheduleDialogOpen(false);
    setSelectedPipeline(null);
  };

  const handleCreateSchedule = () => {
    setEditingSchedule(null);
    setScheduleFormOpen(true);
  };

  const handleEditSchedule = (schedule) => {
    setEditingSchedule(schedule);
    setScheduleFormOpen(true);
  };

  const handleScheduleFormClose = () => {
    setScheduleFormOpen(false);
    setEditingSchedule(null);
  };

  const handleScheduleFormSuccess = () => {
    setScheduleFormOpen(false);
    setEditingSchedule(null);
    setScheduleListKey((prev) => prev + 1); // Force refresh of schedule list
  };

  const handleOpenEdit = (pipeline) => {
    setEditingPipeline(pipeline);
    setEditDialogOpen(true);
  };

  const handleCloseEdit = () => {
    setEditDialogOpen(false);
    setEditingPipeline(null);
  };

  const handleEditSuccess = () => {
    setEditDialogOpen(false);
    setEditingPipeline(null);
    fetchPipelines(); // Refresh list
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Pipeline Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create Pipeline
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Creator</TableCell>
              <TableCell>Cost Center</TableCell>
              <TableCell>Cost/hr</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pipelines.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="textSecondary" py={4}>
                    No pipelines configured. Click "Create Pipeline" to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              pipelines.map((pipeline) => (
                <TableRow key={pipeline.id}>
                  <TableCell>
                    <Typography variant="subtitle2">{pipeline.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={pipeline.type.toUpperCase()} size="small" />
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={pipeline.status} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {pipeline.creator_username || 'admin'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={pipeline.cost_center || 'product_demos'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    ${pipeline.estimated_hourly_cost?.toFixed(2) || '0.00'}
                  </TableCell>
                  <TableCell>
                    {new Date(pipeline.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="center">
                    {pipeline.status === 'idle' && (
                      <Tooltip title="Start Pipeline">
                        <IconButton
                          color="primary"
                          onClick={() => handleStart(pipeline.id)}
                          aria-label="Start Pipeline"
                        >
                          <PlayIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {pipeline.status === 'running' && (
                      <Tooltip title="Stop Pipeline">
                        <IconButton
                          color="warning"
                          onClick={() => handleStop(pipeline.id)}
                          aria-label="Stop Pipeline"
                        >
                          <StopIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Manage Schedules">
                      <IconButton
                        color="info"
                        onClick={() => handleOpenSchedules(pipeline)}
                        aria-label="Manage Schedules"
                      >
                        <ScheduleIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Pipeline">
                      <IconButton
                        color="default"
                        onClick={() => handleOpenEdit(pipeline)}
                        aria-label="Edit Pipeline"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Pipeline">
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(pipeline.id)}
                        disabled={pipeline.status !== 'idle'}
                        aria-label="Delete Pipeline"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <CreatePipelineForm
          onClose={() => setCreateDialogOpen(false)}
          onSuccess={handleCreateSuccess}
        />
      </Dialog>

      {/* Schedule Management Dialog */}
      <Dialog
        open={scheduleDialogOpen}
        onClose={handleCloseSchedules}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Schedule Management
          {selectedPipeline && (
            <Typography variant="subtitle2" color="text.secondary">
              Pipeline: {selectedPipeline.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedPipeline && (
            <ScheduleWindowList
              key={scheduleListKey}
              pipelineId={selectedPipeline.id}
              onEdit={handleEditSchedule}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSchedules}>Close</Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateSchedule}
          >
            Add Schedule
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Form Dialog */}
      {scheduleFormOpen && selectedPipeline && (
        <ScheduleWindowForm
          pipelineId={selectedPipeline.id}
          pipelineName={selectedPipeline.name}
          schedule={editingSchedule}
          onClose={handleScheduleFormClose}
          onSuccess={handleScheduleFormSuccess}
        />
      )}

      {/* Edit Pipeline Dialog */}
      {editDialogOpen && editingPipeline && (
        <EditPipelineForm
          pipeline={editingPipeline}
          onClose={handleCloseEdit}
          onSuccess={handleEditSuccess}
        />
      )}
    </Box>
  );
}

export default Admin;